
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Data/Enums
import { DataModules } from "@/store/modules/data";
import {
  DefaultUsersFilters,
  DefaultApiTokensFilters
} from "@/data/filters/users";
import {
  DefaultUsersSorters,
  DefaultApiTokensSorters
} from "@/data/sorters/users";

import { Sorters } from "@/data/table";

// Types
import {
  IUsersProviderData,
  IUsersProviderMethods
} from "@/models/providers/users";
import { ISorter, IFilter, ITableConfig } from "@/models/tables";
import { IUser } from "@/models/users";

@Component
export default class UsersProvider extends Mixins(FilteringSortingTableStore) {
  // Props

  @Prop({ default: `ULP1` /** [U]sers [L]ist [P]rovider [1]*/ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  @Prop({ default: () => DefaultUsersFilters })
  public availableFilters!: (vm: UsersProvider) => IFilter[];

  @Prop({ default: () => DefaultUsersSorters })
  public availableSorters!: (vm: UsersProvider) => ISorter[];

  @Prop({ default: false })
  public isApiToken!: boolean;

  // Data

  public dataModule = DataModules.USERS;

  public filters: IFilter[] = this.isApiToken
    ? DefaultApiTokensFilters()
    : this.availableFilters(this);
  public sorters: ISorter[] = this.isApiToken
    ? DefaultApiTokensSorters()
    : this.availableSorters(this);

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;

  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public searchQuery = "";

  // Life-cycle Methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }

    await this.getData();
    this.$bus.$on("refreshUsersListing", this.refreshListing);
    this.isLoading = false;
  }

  public beforeDestroy() {
    this.$bus.$off("refreshUsersListing");
  }

  // Methods

  public reloadData() {
    this.isReloading = true;
    return this.refreshListing();
  }

  public async refreshListing() {
    this.isProcessing = true;
    await this.getData({ ignoreStored: true });
    this.isProcessing = false;
  }

  public async updateUser(user: IUser) {
    if (!this.canUpdate && !this.isApiToken) return;

    try {
      this.$store.dispatch(`data/${DataModules.USERS}/openCreateModal`, {
        props: {
          isApiToken: this.isApiToken,
          user
        },
        events: {
          success: () => {
            this.$store.dispatch("toast/show", {
              message: this.$t("_sentence.api_token_updated")
            });
            this.refreshListing();
          }
        }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async changeActive(userId: IUser["id"], isActive: boolean) {
    return this.$store.dispatch(`data/${DataModules.USERS}/update`, {
      id: userId,
      data: {
        active: isActive
      }
    });
  }

  public async revokeApiToken(id: IUser["id"]) {
    if (!this.canRevoke) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.revoke"),
          message: this.$t("_sentence.revoke_token_confirmation")
        },
        events: {
          confirmed: async () => {
            await this.$store.dispatch(
              `data/${DataModules.API_TOKENS}/revoke`,
              { id }
            );
            this.$store.dispatch("toast/show", {
              message: this.$t("_sentence.api_token_revoked")
            });
            this.refreshListing();
            confirm.close();
          }
        }
      }
    });
  }

  public async refreshApiToken({ id, api_token_preview }: IUser) {
    if (!this.canRefresh) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.refresh_token"),
          message: this.$t("_sentence.refresh_token_confirmation", {
            tokenPreview: api_token_preview
          })
        },
        events: {
          confirmed: async () => {
            try {
              this.isProcessing = true;
              const {
                token
              } = await this.$store.dispatch(
                `data/${DataModules.API_TOKENS}/refreshApiToken`,
                { id }
              );
              await Promise.all([
                // Show token
                this.$store.dispatch(
                  `data/${DataModules.API_TOKENS}/show`,
                  token
                ),
                // Refresh listing
                this.refreshListing(),
                // Show toast
                this.$store.dispatch("toast/show", {
                  message: this.$t("_sentence.api_token_refreshed")
                })
              ]);
              confirm.close();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
            }
          }
        }
      }
    });
  }

  public async delete(userId: IUser["id"]) {
    if (!this.canDelete) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.user") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(userId);
            confirm.close();
          }
        }
      }
    });
  }

  public onDeleteConfirmed(userId: IUser["id"]) {
    if (!this.canDelete) return;

    this.table.loading = true;
    return this.$store
      .dispatch(`data/${DataModules.USERS}/remove`, {
        id: userId,
        vm: this
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t("_.object_successfully_removed", {
            object: this.$t("_.user")
          }) as string
        });
        this.refreshListing();
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
        this.table.loading = false;
      });
  }

  public openActivityLogModal(user: IUser) {
    return this.$store.dispatch(
      `data/${DataModules.USERS}/openActivityLogModal`,
      user
    );
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: "userBrandGroup.brand,userBrandGroup.group",
          ...this.queryFilter,
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.getData({
      ignoreStored: true,
      page: 1
    });
  }

  // Computed

  public get canUpdate() {
    return this.$userCan("update_user");
  }

  public get canDelete() {
    return this.$userCan("delete_user");
  }

  public get canRevoke() {
    return this.$userCan("destroy_admin_user_api_token");
  }

  public get canReveal() {
    return this.$userCan("get_admin_user_api_token");
  }

  public get canRefresh() {
    return this.$userCan("refresh_admin_user_api_token");
  }

  public get showEmptyState() {
    return (
      !this.isLoading &&
      !this.isReloading &&
      !this.isProcessing &&
      !this.table.data.length
    );
  }

  public get queryFilter() {
    return this.searchQuery ? { query: this.searchQuery } : {};
  }

  // Guarding

  public get guardFunctionalities() {
    return ["list_users"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("usersProviderData")
  public providerData(): IUsersProviderData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      canDelete: this.canDelete,
      canRefresh: this.canRefresh,
      canReveal: this.canReveal,
      canRevoke: this.canRevoke,
      canUpdate: this.canUpdate,
      config: this.config,
      defaults: this.defaults,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      hasRequestError: false,
      isLoading: this.isLoading || this.table.loading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      searchQuery: this.searchQuery,
      showEmptyState: this.showEmptyState,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total,
      users: this.table.data
    };
  }

  @Provide("usersProviderMethods")
  public get providerMethods(): IUsersProviderMethods {
    return {
      changeActive: this.changeActive,
      debounceSearch: this.debounceSearch,
      delete: this.delete,
      doQuickSearch: this.doQuickSearch,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openActivityLogModal: this.openActivityLogModal,
      refreshApiToken: this.refreshApiToken,
      refreshListing: this.refreshListing,
      reloadData: this.reloadData,
      revokeApiToken: this.revokeApiToken,
      updateUser: this.updateUser
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $usersData: this.providerData(),
        $usersMethods: this.providerMethods
      })
    );
  }
}
